/**
 * Utility functions for managing diff reports, including URL generation,
 * storage operations, database updates, and validation.
 */

import { supabase } from '@/integrations/supabase/client';
import type { JsonReportContent, Trend } from '@/types';

/**
 * Generate consistent published URL for a report
 * Format: published/{period}/summary.json (within 'reports' bucket)
 *
 * @example
 * generatePublishedUrl("2025Q4")  → "published/2025Q4/summary.json"
 * generatePublishedUrl("2025W49") → "published/2025W49/summary.json"
 */
export const generatePublishedUrl = (period: string): string => {
  return `published/${period}/summary.json`;
};

/**
 * Generate versioned draft URL for a report
 * Format: draft/{period}_v{version}_{timestamp}.json (within 'reports' bucket)
 *
 * @example
 * generateDraftUrl("2025Q4", 1) → "draft/2025Q4_v1_1733234567.json"
 * generateDraftUrl("2025Q4", 2) → "draft/2025Q4_v2_1733234890.json"
 */
export const generateDraftUrl = (period: string, version: number = 1): string => {
  const timestamp = Date.now();
  return `draft/${period}_v${version}_${timestamp}.json`;
};

/**
 * Extract bucket name from Supabase storage URL
 * Handles both old and new URL formats
 */
export const extractBucketFromUrl = (url: string): string => {
  // URL format: https://[project].supabase.co/storage/v1/object/public/[bucket]/[path]
  // Or: https://hub.pricingsaas.com/storage/v1/object/public/[bucket]/[path]

  const match = url.match(/\/object\/public\/([^/]+)\//);
  if (match && match[1]) {
    return match[1];
  }

  // Fallback to default bucket
  return 'reports';
};

/**
 * Extract path from Supabase storage URL
 */
export const extractPathFromUrl = (url: string): string => {
  // URL format: https://[domain]/storage/v1/object/public/[bucket]/[path]
  const match = url.match(/\/object\/public\/[^/]+\/(.+)$/);
  if (match && match[1]) {
    return match[1];
  }

  // If no match, return URL as-is
  return url;
};

/**
 * Upload report content to Supabase storage
 *
 * @param content - The report content to upload
 * @param path - Storage path (e.g., "reports/published/2025Q4/summary.json")
 * @param bucket - Storage bucket name (defaults to "reports")
 * @returns Public URL of uploaded file
 */
export const uploadReportToStorage = async (
  content: JsonReportContent,
  path: string,
  bucket: string = 'reports'
): Promise<string> => {
  try {
    const jsonString = JSON.stringify(content, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });

    const { error: uploadError } = await supabase.storage
      .from(bucket)
      .upload(path, blob, {
        contentType: 'application/json',
        upsert: true  // Overwrite if exists (for republishing)
      });

    if (uploadError) {
      console.error('[uploadReportToStorage] Upload error:', uploadError);
      throw new Error(`Failed to upload: ${uploadError.message}`);
    }

    // Get public URL
    const { data: urlData } = supabase.storage
      .from(bucket)
      .getPublicUrl(path);

    if (!urlData?.publicUrl) {
      throw new Error('Failed to get public URL after upload');
    }

    return urlData.publicUrl;
  } catch (error) {
    console.error('[uploadReportToStorage] Error:', error);
    throw error;
  }
};

/**
 * Copy draft report to published location
 * Fetches content from draft URL and uploads to consistent published URL
 *
 * @param draftUrl - URL of the draft report
 * @param period - Period identifier (e.g., "2025Q4")
 * @returns Public URL of published report
 */
export const copyDraftToPublished = async (
  draftUrl: string,
  period: string
): Promise<string> => {
  try {
    // 1. Fetch draft content
    const response = await fetch(draftUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch draft: ${response.statusText}`);
    }
    const draftContent: JsonReportContent = await response.json();

    // 2. Generate published path
    const publishedPath = generatePublishedUrl(period);

    // 3. Upload to published location
    const publishedUrl = await uploadReportToStorage(draftContent, publishedPath);

    return publishedUrl;
  } catch (error) {
    console.error('[copyDraftToPublished] Error:', error);
    throw error;
  }
};

/**
 * Delete published report from storage
 *
 * @param publishedUrl - URL of the published report to delete
 */
export const deletePublishedReport = async (
  publishedUrl: string
): Promise<void> => {
  try {
    const bucket = extractBucketFromUrl(publishedUrl);
    const path = extractPathFromUrl(publishedUrl);

    const { error } = await supabase.storage
      .from(bucket)
      .remove([path]);

    if (error) {
      console.error('[deletePublishedReport] Delete error:', error);
      throw new Error(`Failed to delete: ${error.message}`);
    }
  } catch (error) {
    console.error('[deletePublishedReport] Error:', error);
    throw error;
  }
};

/**
 * Update report URLs and publishing metadata in database
 *
 * @param reportId - UUID of the report
 * @param updates - Fields to update
 */
export const updateReportUrls = async (
  reportId: string,
  updates: {
    draft_url?: string;
    published_url?: string;
    published?: boolean;
    published_at?: string;
    companies_count?: number;
    diffs_count?: number;
    trends_count?: number;
    events_count?: number;
  }
): Promise<void> => {
  try {
    const updateData: any = {
      ...updates,
      updated_at: new Date().toISOString()
    };

    const { error } = await supabase
      .from('app_diff_reports')
      .update(updateData)
      .eq('id', reportId);

    if (error) {
      console.error('[updateReportUrls] Database update error:', error);
      throw new Error(`Failed to update database: ${error.message}`);
    }
  } catch (error) {
    console.error('[updateReportUrls] Error:', error);
    throw error;
  }
};

/**
 * Validation result interface
 */
export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

/**
 * Validate report content before saving
 * Ensures all required fields are present and meet constraints
 *
 * @param content - Report content to validate
 * @returns Validation result with errors if any
 */
export const validateReportContent = (
  content: JsonReportContent
): ValidationResult => {
  const errors: string[] = [];

  // Must have at least 1 trend
  if (!content.trends || content.trends.length === 0) {
    errors.push("Report must contain at least one trend");
  }

  // Validate each trend
  content.trends?.forEach((trend, index) => {
    const trendNum = index + 1;

    // Name required, max 200 chars
    if (!trend.name || trend.name.trim().length === 0) {
      errors.push(`Trend ${trendNum}: Name is required`);
    }
    if (trend.name && trend.name.length > 200) {
      errors.push(`Trend ${trendNum}: Name too long (max 200 characters, current: ${trend.name.length})`);
    }

    // Description required, max 500 chars
    if (!trend.description || trend.description.trim().length === 0) {
      errors.push(`Trend ${trendNum}: Description is required`);
    }
    if (trend.description && trend.description.length > 500) {
      errors.push(`Trend ${trendNum}: Description too long (max 500 characters, current: ${trend.description.length})`);
    }

    // Must have at least 1 example
    if (!trend.examples || trend.examples.length === 0) {
      errors.push(`Trend ${trendNum} "${trend.name}": Must have at least one example`);
    }
  });

  return {
    valid: errors.length === 0,
    errors
  };
};

/**
 * Upload report to storage with retry logic
 * Attempts upload multiple times with exponential backoff
 *
 * @param content - Report content to upload
 * @param path - Storage path
 * @param retries - Number of retries (default: 3)
 * @returns Public URL of uploaded report
 */
export const uploadWithRetry = async (
  content: JsonReportContent,
  path: string,
  retries: number = 3
): Promise<string> => {
  for (let i = 0; i < retries; i++) {
    try {
      return await uploadReportToStorage(content, path);
    } catch (error) {
      if (i === retries - 1) {
        // Last retry failed
        throw error;
      }
      // Wait before retry (exponential backoff: 1s, 2s, 3s)
      await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
    }
  }

  throw new Error("Upload failed after retries");
};

/**
 * Calculate updated metadata based on current trends
 *
 * @param trends - Array of trends
 * @param originalMetadata - Original metadata to merge with
 * @returns Updated metadata object
 */
export const calculateMetadata = (
  trends: Trend[],
  originalMetadata: JsonReportContent['metadata']
): JsonReportContent['metadata'] => {
  const uniqueCompanies = new Set(
    trends.flatMap(trend => trend.examples.map(ex => ex.slug))
  );

  const totalEvents = trends.reduce(
    (sum, trend) => sum + trend.examples.length,
    0
  );

  return {
    ...originalMetadata,
    trendsCount: trends.length,
    eventCount: totalEvents,
    companiesCount: uniqueCompanies.size
  };
};

/**
 * Deep clone JSON content to prevent mutations
 *
 * @param content - Content to clone
 * @returns Deep clone of content
 */
export const cloneReportContent = (content: JsonReportContent): JsonReportContent => {
  return JSON.parse(JSON.stringify(content));
};
